// Client-instance compute node. When enabled, this browser acts as a compute
// worker for its OWN user: it polls the server for jobs the user routed with
// ?via=client (typically GPU / full-engine work the server can't run), executes
// them on the real engine + WebGPU, and posts the result back. Strictly
// own-instance - the server only ever hands a user their own jobs.
//
// ENVELOPE (E2E) MODE
// -------------------
// A job may carry an OPAQUE encrypted payload (job.enc === true): job.input is
// ciphertext sealed under the worklog TEAM KEY, not plaintext engine input. For
// such a job this node:
//   (a) obtains the team key ONLY because the user actively passed it in-process
//       (the "Unlock sealed jobs" action below) - NEVER a silent localStorage
//       read on the user's behalf,
//   (b) decrypts job.input under that key,
//   (c) runs the engine on the plaintext,
//   (d) re-encrypts the result under the same key,
//   (e) posts back the OPAQUE ciphertext result.
// If the user has NOT armed a key in-process, the node never claims an enc job
// (it asks the server for plaintext-only work) and never runs one - no plaintext
// ever leaves this client, no error spam.
//
// The server (queue + bridge) sees only: job id, engine fn, tier, timing, owner,
// and the ciphertext SIZE. It never sees the sealed params/result and never sees
// the key.
//
// The crypto here MIRRORS worklog.js's team-key contract EXACTLY (so a result
// this node seals opens in the worklog page and vice-versa):
//   blob shape : { v:2, alg:'team-aes-gcm', iv:<b64>, ct:<b64> }
//   key        : PBKDF2-HMAC-SHA256, 210000 iters, salt = APP_SALT|teamId, AES-256-GCM
//   passphrase : cached by worklog.js at localStorage['wl_teamkey_'+teamId]
//   AAD        : a per-purpose domain string (see COMPUTE_*_AAD below)
// Keep these in lockstep with worklog.js TEAM_KEY_PBKDF2_ITERATIONS /
// TEAM_KEY_APP_SALT / teamKeyStorageKey / sealUnderTeamKey / openUnderTeamKey.
import { getUser, getLicense } from './auth.js';
import * as events from './events.js';

function token() { return localStorage.getItem('gdbs_token') || ''; }

let _wasm = null, _on = false, _timer = null, _busy = false, _evOff = null;

async function wasm() {
  if (!_wasm) {
    const m = await import('../pkg/gdbs_web_client.js');
    try { await m.default(); } catch (_) {}
    _wasm = m;
  }
  return _wasm;
}

async function authed(path, opts = {}) {
  opts.headers = Object.assign({ 'Authorization': 'Bearer ' + token() }, opts.headers || {});
  opts.cache = 'no-store';
  return fetch(path, opts);
}

// ===========================================================================
// Team-key crypto (mirror of worklog.js - see header). The team passphrase is
// the user's secret; we hold a DERIVED key in memory ONLY for the session and
// ONLY after the user actively passed the passphrase via armSealedKey(). We do
// NOT silently read localStorage['wl_teamkey_*'] here.
// ===========================================================================
const TEAM_KEY_PBKDF2_ITERATIONS = 210000;            // == worklog.js
const TEAM_KEY_APP_SALT = 'vaultsync/worklog/teamkey/v1'; // == worklog.js TEAM_KEY_APP_SALT
const COMPUTE_INPUT_AAD = 'worklog/compute/params/v1';   // sealed engine params
const COMPUTE_RESULT_AAD = 'worklog/compute/result/v1';  // sealed engine result
const te = new TextEncoder();
const td = new TextDecoder();

// In-memory armed keys for THIS session only: teamId -> CryptoKey. Populated by
// armSealedKey() when the USER passes the passphrase. Never persisted here, never
// read back from localStorage by this module.
const _armedKeys = new Map();

function b64(bytes) { return btoa(String.fromCharCode.apply(null, Array.from(bytes))); }
function unb64(s) { return Uint8Array.from(atob(s), c => c.charCodeAt(0)); }

function saltFor(teamId) { return te.encode(TEAM_KEY_APP_SALT + '|' + String(teamId == null ? '' : teamId)); }

// Derive the AES-GCM key from a passphrase the USER supplied. Memoized per team
// for the session. Returns the CryptoKey.
async function deriveTeamKey(teamId, passphrase) {
  const base = await crypto.subtle.importKey('raw', te.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: saltFor(teamId), iterations: TEAM_KEY_PBKDF2_ITERATIONS, hash: 'SHA-256' },
    base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

async function sealBlob(key, plaintext, aad) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: te.encode(aad), tagLength: 128 }, key, te.encode(plaintext)));
  return { v: 2, alg: 'team-aes-gcm', iv: b64(iv), ct: b64(ct) };
}

async function openBlob(key, blob, aad) {
  if (!blob || !blob.iv || !blob.ct) throw new Error('empty envelope');
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: unb64(blob.iv), additionalData: te.encode(aad), tagLength: 128 },
    key, unb64(blob.ct));
  return td.decode(pt);
}

// PUBLIC: the user actively passes the team key in-process to enable sealed jobs
// for this session. teamId scopes the PBKDF2 salt (must match the team that
// sealed the job). Returns true if a key was armed. NOTHING is persisted by this
// module; the derived key lives in memory until disarm/reload.
export async function armSealedKey(teamId, passphrase) {
  if (!passphrase || !String(passphrase).trim()) return false;
  const key = await deriveTeamKey(String(teamId == null ? '' : teamId), passphrase);
  _armedKeys.set(String(teamId == null ? '' : teamId), key);
  setStatus(_on ? 'on - sealed armed' : 'sealed armed');
  return true;
}

// PUBLIC: forget all in-process armed keys (e.g. on lock / logout).
export function disarmSealedKeys() {
  _armedKeys.clear();
  setStatus(_on ? 'on' : 'off');
}

function hasArmedKey() { return _armedKeys.size > 0; }

// The team id an enc job is sealed under. The job carries it via a small hint in
// the ciphertext envelope wrapper (see worklog.js dispatch contract): the sealed
// input is { teamId, blob }. We never need the teamId in cleartext on the server;
// it rides INSIDE the opaque payload the client produced, and the only thing the
// node needs is which armed key to try. If a single key is armed we use it; if
// several are armed we match by the envelope's teamId.
function pickKeyForEnvelope(env) {
  if (env && env.teamId != null && _armedKeys.has(String(env.teamId))) return _armedKeys.get(String(env.teamId));
  if (_armedKeys.size === 1) return Array.from(_armedKeys.values())[0];
  return null;
}

async function report(id, result, error) {
  try {
    await authed('/api/clientjobs/' + encodeURIComponent(id) + '/result', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ result, error })
    });
  } catch (_) {}
}

// Run a plaintext (legacy) job.
async function runPlain(job) {
  const w = await wasm();
  const fn = w[job.function];
  if (typeof fn !== 'function') { await report(job.id, null, 'Unknown engine function: ' + job.function); return; }
  let r = fn(job.input || '{}');
  if (r instanceof Promise) r = await r;            // GPU / async entry points
  const result = (typeof r === 'string') ? r : JSON.stringify(r);
  await report(job.id, result, null);
}

// Run an ENVELOPE (enc) job: decrypt-run-reencrypt, never emitting plaintext.
async function runEnc(job) {
  // job.input is the opaque envelope wrapper { teamId, blob } produced client-side.
  let env;
  try { env = JSON.parse(job.input || '{}'); } catch (_) { env = null; }
  const key = pickKeyForEnvelope(env);
  if (!key) {
    // The user has not armed the right key in-process. SKIP HONESTLY: do not run,
    // do not post plaintext, do not spam errors. The job stays claimed; once the
    // user arms the key, future dispatches run. (We intentionally leave it - the
    // worklog side surfaces "needs key" to the user, Agent D.)
    return false;
  }
  let plain;
  try { plain = await openBlob(key, env.blob, COMPUTE_INPUT_AAD); }
  catch (e) {
    // Wrong/rotated key: we MUST NOT run on a failed decrypt and MUST NOT leak.
    // Report a non-sensitive error so the run does not hang forever.
    await report(job.id, null, 'sealed-decrypt-failed');
    return true;
  }
  const w = await wasm();
  const fn = w[job.function];
  if (typeof fn !== 'function') { await report(job.id, null, 'Unknown engine function: ' + job.function); return true; }
  let r = fn(plain || '{}');
  if (r instanceof Promise) r = await r;
  const resultPlain = (typeof r === 'string') ? r : JSON.stringify(r);
  // Re-seal the result under the SAME key, same teamId, result AAD.
  const teamId = (env && env.teamId != null) ? String(env.teamId) : '';
  const sealed = await sealBlob(key, resultPlain, COMPUTE_RESULT_AAD);
  // The opaque result envelope mirrors the input wrapper { teamId, blob }.
  await report(job.id, JSON.stringify({ teamId, blob: sealed }), null);
  return true;
}

async function runOne(job) {
  try {
    if (job.enc) { await runEnc(job); return; }
    await runPlain(job);
  } catch (e) {
    await report(job.id, null, String((e && e.message) || e));
  }
}

// Claim and run a single job; returns true if a job was run (so an event-driven
// drain can keep going until the queue is empty).
async function tick() {
  if (!_on || _busy) return false;
  _busy = true;
  let ran = false;
  try {
    // Only ask for enc work if the user has armed a key in-process this session;
    // otherwise the server hands us plaintext-only jobs so we never claim-and-
    // strand a sealed job we cannot decrypt.
    const wantEnc = hasArmedKey();
    const res = await authed('/api/clientjobs/next?enc=' + (wantEnc ? 'true' : 'false'));
    if (res.ok) {
      const d = await res.json();
      if (d && d.job) { setStatus('running ' + d.job.function + '...'); await runOne(d.job); setStatus(wantEnc ? 'on - sealed armed' : 'on'); ran = true; }
    }
  } catch (_) {} finally { _busy = false; }
  return ran;
}

// Event-driven path: a 'compute-job' push means the user routed at least one job
// to this client. Drain the queue (a single push may cover several queued jobs)
// until empty or the node is turned off. Bounded so a misbehaving stream can't
// spin forever in one turn.
async function drain() {
  if (!_on) return;
  for (let i = 0; i < 64; i++) {
    const ran = await tick();
    if (!ran || !_on) break;
  }
}

function setStatus(s) { const el = document.getElementById('compute-node-status'); if (el) el.textContent = s; }
function updateUi() {
  const lbl = document.getElementById('compute-node-label');
  if (lbl) lbl.textContent = _on ? 'Compute node: ON' : 'Compute node: off';
}

function start() {
  if (_on) return;
  _on = true;
  localStorage.setItem('gdbs_compute_node', '1');
  // Event-driven: claim-and-run as soon as the server pushes a 'compute-job' for
  // this user, instead of polling the bridge every 4s.
  try { _evOff = events.on('compute-job', () => { drain().catch(() => {}); }); } catch (_) { _evOff = null; }
  // Slow safety timer: if the push stream is down, this still picks up routed
  // jobs (just less promptly than the old 4s loop). 30s.
  _timer = setInterval(drain, 30000);
  drain();
  updateUi(); setStatus(hasArmedKey() ? 'on - sealed armed' : 'on');
}
function stop() {
  _on = false;
  localStorage.removeItem('gdbs_compute_node');
  if (_timer) clearInterval(_timer);
  _timer = null;
  if (_evOff) { try { _evOff(); } catch (_) {} _evOff = null; }
  // Forget in-process keys when the node is turned off.
  _armedKeys.clear();
  updateUi(); setStatus('off');
}
function toggle() { _on ? stop() : start(); }

// Reveal the toggle to authenticated users; auto-resume if previously enabled.
export function applyComputeNode(user, license) {
  const b = document.getElementById('btn-compute-node');
  if (b) b.classList.remove('hidden');
  updateUi();
  if (localStorage.getItem('gdbs_compute_node') === '1' && !_on) start();
}

export function initComputeNode() {
  document.getElementById('btn-compute-node')?.addEventListener('click', toggle);
  window.gdbsToggleComputeNode = toggle;
  // Expose the in-process arm/disarm so a key-prompt UX (the worklog page or a
  // main-app unlock affordance) can pass the user's team key WITHOUT this module
  // ever reading it from storage on the user's behalf.
  window.gdbsArmSealedJobs = armSealedKey;
  window.gdbsDisarmSealedJobs = disarmSealedKeys;
}
